import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Briefcase, MapPin, Calendar, User, ArrowLeft, Home, Send, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const AnuncioDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [anuncio, setAnuncio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAnuncio();
  }, [id]);

  const fetchAnuncio = async () => {
    try {
      const response = await fetch(`${API}/anuncios/${id}`);
      if (response.ok) {
        const data = await response.json();
        setAnuncio(data);
      } else {
        toast.error('Anuncio no encontrado');
        navigate('/anuncios');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el anuncio');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Debes iniciar sesión para contactar');
      navigate('/auth');
      return;
    }

    if (!message.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/anuncios/${id}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: message.trim() })
      });

      if (response.ok) {
        toast.success('Mensaje enviado correctamente');
        setMessage('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al enviar mensaje');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      'ofrecer-servicios': 'Ofrezco servicios',
      'buscar-servicios': 'Busco servicios',
      'otros': 'Otros'
    };
    return labels[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'ofrecer-servicios': 'bg-green-100 text-green-700',
      'buscar-servicios': 'bg-blue-100 text-blue-700',
      'otros': 'bg-gray-100 text-gray-700'
    };
    return colors[cat] || 'bg-gray-100';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const daysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Cargando anuncio...</p>
        </div>
      </div>
    );
  }

  if (!anuncio) return null;

  const isOwner = user && user.id === anuncio.user_id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} className="hidden sm:flex">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Button>
              <Button variant="ghost" onClick={() => navigate('/anuncios')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anuncios
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-lg">
          <CardContent className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <Badge className={`${getCategoryColor(anuncio.category)} text-sm px-3 py-1`}>
                {getCategoryLabel(anuncio.category)}
              </Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                {daysRemaining(anuncio.expires_at) > 0 
                  ? `Expira en ${daysRemaining(anuncio.expires_at)} días`
                  : 'Expira hoy'}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              {anuncio.title}
            </h1>

            {/* Meta info */}
            <div className="flex flex-wrap gap-4 text-slate-600 mb-6 pb-6 border-b">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-slate-400" />
                <span>{anuncio.user_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span>{anuncio.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <span>Publicado el {formatDate(anuncio.created_at)}</span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="font-semibold text-lg mb-3">Descripción</h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {anuncio.description}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            {!isOwner && (
              <div className="bg-slate-50 rounded-xl p-6">
                <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Send className="w-5 h-5 text-red-600" />
                  Contactar con el anunciante
                </h2>
                
                {user ? (
                  <form onSubmit={handleSendMessage}>
                    <Textarea
                      placeholder="Escribe tu mensaje aquí... El anunciante recibirá tu mensaje de forma privada."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="mb-4 min-h-[120px]"
                      maxLength={1000}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">
                        {message.length}/1000 caracteres
                      </span>
                      <Button 
                        type="submit" 
                        className="bg-red-600 hover:bg-red-700"
                        disabled={sending || !message.trim()}
                      >
                        {sending ? 'Enviando...' : 'Enviar mensaje'}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-600 mb-3">Inicia sesión para contactar con el anunciante</p>
                    <Link to="/auth">
                      <Button className="bg-red-600 hover:bg-red-700">
                        Iniciar sesión
                      </Button>
                    </Link>
                  </div>
                )}

                <div className="mt-4 flex items-start gap-2 text-sm text-slate-500">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>Tu mensaje será enviado de forma privada. No compartimos tu email ni teléfono con el anunciante.</p>
                </div>
              </div>
            )}

            {/* Owner notice */}
            {isOwner && (
              <div className="bg-blue-50 rounded-xl p-6 text-center">
                <p className="text-blue-700">Este es tu anuncio. Los mensajes que recibas aparecerán en "Mis Anuncios".</p>
                <Link to="/mis-anuncios">
                  <Button variant="outline" className="mt-3">
                    Ver mis mensajes
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnuncioDetailPage;
